import type {
  Account,
  Configuration,
  RestoreFilesRequest,
  RestoreDbToRdsInstanceRequest,
  RestoreBucketRequest,
  RestoreAtlasClusterInput,
  RestoreAwsEc2InstanceRequest,
  ListAzureStorageAccountContainers200Response,
  RestoreImageInput,
  RestoreAzureVmInstanceRequest,
  RestoreAzureDiskRequest,
  RestoreVolumeToEbsRequest,
  ListAzureSKUs200Response,
  ResourceType,
  RestoreEksNamespaceInput,
  RestoreAzureSqlDatabaseRequest,
  RestoreGcpDiskRequest,
  RestoreGcpVmInstanceRequest,
  RestoreDynamoDBTableRequest,
  RestoreGcpCloudSqlRequest,
} from '@repo/api-gw-sdk';
import {
  SnapshotsApi,
  ListS3BucketsApi,
  ListAvailabilityZonesApi,
  ListEncryptionKeysApi,
  ListRestoreRegionsApi,
  ListAwsSecurityGroupsApi,
  ListAwsRdsInstanceClassesApi,
  ListAwsRdsSubnetGroupsApi,
  ListAtlasProjectsApi,
  RestoreAtlasClusterApi,
  ListAwsInstanceTypesApi,
  ListAwsSubnetsV2Api,
  ListAzureStorageAccountsApi,
  ListAzureStorageAccountContainersApi,
  RestoreImageApi,
  ListAzureResourceGroupsApi,
  ListAzureUnassignedNetworkInterfacesApi,
  ListAzureSKUsApi,
  RestoreEksNamespaceApi,
  ListGCSBucketsApi,
  ListAzureRegionsApi,
  ListGcpEncryptionKeysApi,
  CheckAzureVMNameExistsApi,
  ListGcpMachineTypesApi,
  Provider,
  ListGcpSubnetsApi,
  ListGcpSharedSubnetsApi,
  ListGcpNetworksApi,
  ListGcpDiskTypesApi,
} from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/restore';

export const restore = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listS3BucketsApi = new ListS3BucketsApi(config);
  const snapshotsApi = new SnapshotsApi(config);
  const listEncryptionKeysApi = new ListEncryptionKeysApi(config);
  const listAwsSecurityGroupsApi = new ListAwsSecurityGroupsApi(config);
  const listAwsRdsInstanceClassesApi = new ListAwsRdsInstanceClassesApi(config);
  const listRestoreRegionsApi = new ListRestoreRegionsApi(config);
  const listAvailabilityZonesApi = new ListAvailabilityZonesApi(config);
  const listAtlasProjectsApi = new ListAtlasProjectsApi(config);
  const restoreAtlasClusterApi = new RestoreAtlasClusterApi(config);
  const listAwsRdsSubnetGroupsApi = new ListAwsRdsSubnetGroupsApi(config);
  const restoreImageApi = new RestoreImageApi(config);
  const listAwsInstanceTypesApi = new ListAwsInstanceTypesApi(config);
  const listAwsSubnetsV2Api = new ListAwsSubnetsV2Api(config);
  const listAzureResourceGroupsApi = new ListAzureResourceGroupsApi(config);
  const listAzureNetworkInterfacesApi =
    new ListAzureUnassignedNetworkInterfacesApi(config);
  const listAzureStorageAccountsApi = new ListAzureStorageAccountsApi(config);
  const listAzureStorageAccountContainersApi =
    new ListAzureStorageAccountContainersApi(config);
  const listAzureSKUSApi = new ListAzureSKUsApi(config);
  const restoreEksNamespaceApi = new RestoreEksNamespaceApi(config);
  const listGCSBucketsApi = new ListGCSBucketsApi(config);
  const listAzureRegionsApi = new ListAzureRegionsApi(config);
  const checkAzureVMNameExistsApi = new CheckAzureVMNameExistsApi(config);
  const listGcpMachineTypesApi = new ListGcpMachineTypesApi(config);
  const listGcpEncryptionKeysApi = new ListGcpEncryptionKeysApi(config);
  const listGcpSubnetsApi = new ListGcpSubnetsApi(config);
  const listGcpSharedSubnetsApi = new ListGcpSharedSubnetsApi(config);
  const listGcpDiskTypesApi = new ListGcpDiskTypesApi(config);
  const listGcpNetworksApi = new ListGcpNetworksApi(config);

  return {
    volume: (
      resourceId: string,
      snapshotId: string,
      payload: RestoreVolumeToEbsRequest
    ) =>
      snapshotsApi.restoreEbsVolume(
        currentProjectId,
        resourceId,
        snapshotId,
        payload
      ),
    files: (
      resourceId: string,
      snapshotId: string,
      restoreFilesInput: RestoreFilesRequest
    ) =>
      snapshotsApi.restoreFiles(
        currentProjectId,
        resourceId,
        snapshotId,
        restoreFilesInput
      ),
    database: (
      id: string,
      snapshotId: string,
      restoreDatabaseInput: RestoreDbToRdsInstanceRequest
    ) =>
      snapshotsApi.restoreDatabase(
        currentProjectId,
        id,
        snapshotId,
        restoreDatabaseInput
      ),
    dynamoDB: (
      id: string,
      snapshotId: string,
      restoreDynamoDBInput: RestoreDynamoDBTableRequest
    ) =>
      snapshotsApi.restoreDynamoDBTable(
        currentProjectId,
        id,
        snapshotId,
        restoreDynamoDBInput
      ),
    encryptionKeys: {
      list: (
        cloudAccountId: string | undefined,
        regionName: string | undefined,
        includeAwsManagedKeys?: boolean
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<typeof listEncryptionKeysApi.listEncryptionKeys>
          ) =>
            !cloudAccountId || !regionName
              ? Promise.resolve(undefined)
              : listEncryptionKeysApi.listEncryptionKeys(...args),

          baseUrl,
          '/encryptionKeys',
          cloudAccountId || '',
          regionName || '',
          currentProjectId,
          includeAwsManagedKeys
        ),
    },

    regions: {
      list: (cloudAccountId: string) =>
        httpClient.execute(
          listRestoreRegionsApi.listRestoreRegions.bind(listRestoreRegionsApi),
          baseUrl,
          '/regions',
          cloudAccountId,
          currentProjectId
        ),
      listMany: (cloudAccounts: Account[]) =>
        httpClient.execute(
          (cloudAccounts: Account[], projectId: string) =>
            Promise.all(
              cloudAccounts.map((cloudAccount) => {
                if (
                  cloudAccount.cloudProvider === Provider.Aws ||
                  cloudAccount.cloudProvider === Provider.Gcp
                ) {
                  return listRestoreRegionsApi.listRestoreRegions(
                    cloudAccount.id,
                    projectId
                  );
                } else if (cloudAccount.cloudProvider === Provider.Azure) {
                  return listAzureRegionsApi.listAzureRegions(projectId);
                } else {
                  return Promise.resolve({ regions: [] });
                }
              })
            ).then((results) => {
              const regions = new Set<string>();
              results.forEach((result) =>
                result.regions.forEach((region) => regions.add(region))
              );

              return {
                regions: Array.from(regions),
              };
            }),
          baseUrl,
          '/regions',
          cloudAccounts,
          currentProjectId
        ),
    },
    availabilityZones: {
      list: (cloudAccountId: string, region?: string) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAvailabilityZonesApi.listAvailabilityZones
            >
          ) =>
            !region
              ? Promise.resolve(undefined)
              : listAvailabilityZonesApi.listAvailabilityZones(...args),
          baseUrl,
          '/regions/availabilityZones',
          cloudAccountId,
          region || '',
          currentProjectId
        ),
    },
    s3Buckets: {
      list: (account?: Account) =>
        httpClient.execute(
          (...args: Parameters<typeof listS3BucketsApi.listS3Buckets>) =>
            account?.id && account?.cloudProvider === Provider.Aws
              ? listS3BucketsApi.listS3Buckets(...args)
              : Promise.resolve(undefined),
          baseUrl,
          '/buckets',
          account?.id || '',
          currentProjectId
        ),
      restore: (
        resourceId: string,
        snapshotId: string,
        restoreBucketInput: RestoreBucketRequest
      ) =>
        snapshotsApi.restoreBucket(
          currentProjectId,
          resourceId,
          snapshotId,
          restoreBucketInput
        ),
    },
    gcsBuckets: {
      list: (account?: Account) =>
        httpClient.execute(
          (...args: Parameters<typeof listGCSBucketsApi.listGCSBuckets>) =>
            account?.id && account?.cloudProvider === Provider.Gcp
              ? listGCSBucketsApi.listGCSBuckets(...args)
              : Promise.resolve(undefined),
          baseUrl,
          '/gcs-buckets',
          account?.id || '',
          currentProjectId
        ),
    },
    gcp: {
      listNetworks: (accountId: string) =>
        httpClient.execute(
          listGcpNetworksApi.listGcpNetworks.bind(listGcpNetworksApi),
          baseUrl,
          '/gcp-networks',
          accountId,
          currentProjectId
        ),
      listSubnets: (accountId: string, region: string) =>
        httpClient.execute(
          listGcpSubnetsApi.listGcpSubnets.bind(listGcpSubnetsApi),
          baseUrl,
          '/gcp-subnets',
          accountId,
          region,
          currentProjectId
        ),
      listSharedSubnets: (accountId: string, region: string) =>
        httpClient.execute(
          listGcpSharedSubnetsApi.listGcpSharedSubnets.bind(
            listGcpSharedSubnetsApi
          ),
          baseUrl,
          '/gcp-shared-subnets',
          accountId,
          region,
          currentProjectId
        ),
      listDiskTypes: (accountId: string, zone: string, machineType?: string) =>
        httpClient.execute(
          listGcpDiskTypesApi.listGcpDiskTypes.bind(listGcpDiskTypesApi),
          baseUrl,
          '/gcp-disk-types',
          accountId,
          zone,
          currentProjectId,
          machineType
        ),
    },
    azureRestoreConfig: {
      listRegions: () =>
        httpClient.execute(
          listAzureRegionsApi.listAzureRegions.bind(listAzureRegionsApi),
          baseUrl,
          '/azure-regions',
          currentProjectId
        ),
      listResourceGroups: (accountId: string) =>
        httpClient.execute(
          listAzureResourceGroupsApi.listAzureResourceGroups.bind(
            listAzureResourceGroupsApi
          ),
          baseUrl,
          '/restore-resource-groups',
          accountId,
          currentProjectId
        ),
      listNetworkInterfaces: (accountId: string, resourceGroup: string) =>
        httpClient.execute(
          listAzureNetworkInterfacesApi.listAzureUnassignedNetworkInterfaces.bind(
            listAzureNetworkInterfacesApi
          ),
          baseUrl,
          '/networkInterfaces',
          accountId,
          currentProjectId,
          { resourceGroup: resourceGroup }
        ),
      listSKUs: (
        accountId: string,
        location: string,
        resourceType:
          | ResourceType.AzureVirtualMachine
          | ResourceType.AzureSqlVirtualMachine
          | ResourceType.AzureDisk
          | ResourceType.AzureSqlDatabase
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof listAzureSKUSApi.listAzureSKUs>) =>
            location
              ? listAzureSKUSApi.listAzureSKUs(...args)
              : Promise.resolve({ skus: [] } as ListAzureSKUs200Response),

          baseUrl,
          '/SKUS',
          accountId,
          currentProjectId,
          {
            location,
            resourceType,
          }
        ),
    },
    storageAccounts: {
      list: (accountId: string) =>
        httpClient.execute(
          listAzureStorageAccountsApi.listAzureStorageAccounts.bind(
            listAzureStorageAccountsApi
          ),
          baseUrl,
          '/storageAccounts',
          accountId,
          currentProjectId
        ),
      listContainers: (
        accountId: string,
        storageAccountName: string,
        resourceGroup: string
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAzureStorageAccountContainersApi.listAzureStorageAccountContainers
            >
          ) =>
            !storageAccountName || !resourceGroup
              ? Promise.resolve({
                  containers: [],
                } as ListAzureStorageAccountContainers200Response)
              : listAzureStorageAccountContainersApi.listAzureStorageAccountContainers(
                  ...args
                ),
          baseUrl,
          '/storageAccounts/containers',
          accountId,
          currentProjectId,
          {
            storageAccount: {
              name: storageAccountName,
              resourceGroup: resourceGroup,
              location: '',
            },
          }
        ),
    },
    securityGroups: {
      list: (
        cloudAccountId: string,
        region?: string,
        vpc?: string,
        subnetId?: string
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAwsSecurityGroupsApi.listAwsSecurityGroups
            >
          ) =>
            !region
              ? Promise.resolve(undefined)
              : listAwsSecurityGroupsApi.listAwsSecurityGroups(...args),
          baseUrl,
          '/regions/aws-security-groups',
          cloudAccountId,
          region || '',
          currentProjectId,
          vpc,
          subnetId
        ),
    },
    rdsInstanceClasses: {
      list: (
        cloudAccountId: string,
        engine: string,
        engineVersion: string,
        region: string
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAwsRdsInstanceClassesApi.listAwsRdsInstanceClasses
            >
          ) => listAwsRdsInstanceClassesApi.listAwsRdsInstanceClasses(...args),
          baseUrl,
          '/regions/aws-rds-instance-classes',
          engine,
          engineVersion,
          cloudAccountId,
          region,
          currentProjectId
        ),
    },
    rdsSubnetGroup: {
      list: (cloudAccountId: string, region: string) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAwsRdsSubnetGroupsApi.listAwsRdsSubnetGroups
            >
          ) =>
            !region
              ? Promise.resolve(undefined)
              : listAwsRdsSubnetGroupsApi.listAwsRdsSubnetGroups(...args),
          baseUrl,
          '/regions/aws-rds-subnet-groups',
          cloudAccountId,
          region,
          currentProjectId
        ),
    },
    atlasProjects: {
      list: (accountId: string) =>
        httpClient.execute(
          listAtlasProjectsApi.listAtlasProjects.bind(listAtlasProjectsApi),
          baseUrl,
          '/atlasProjects',
          accountId,
          currentProjectId
        ),
      restore: (
        resourceId: string,
        snapshotId: string,
        restoreAtlasClusterInput: RestoreAtlasClusterInput
      ) =>
        restoreAtlasClusterApi.restoreAtlasCluster(
          currentProjectId,
          resourceId,
          snapshotId,
          restoreAtlasClusterInput
        ),
    },
    instanceTypes: {
      list: (cloudAccountId: string, region: string, subnetId: string) =>
        httpClient.execute(
          listAwsInstanceTypesApi.listAwsInstanceTypes.bind(
            listAwsInstanceTypesApi
          ),
          baseUrl,
          '/listAwsInstanceTypes',
          cloudAccountId,
          region,
          currentProjectId,
          subnetId
        ),
    },
    awsSubnets: {
      list: (cloudAccountId: string, region?: string) =>
        httpClient.execute(
          (...args: Parameters<typeof listAwsSubnetsV2Api.listAwsSubnetsV2>) =>
            !region
              ? Promise.resolve(undefined)
              : listAwsSubnetsV2Api.listAwsSubnetsV2(...args),
          baseUrl,
          '/listAwsSubnets',
          cloudAccountId,
          region || '',
          currentProjectId
        ),
    },
    ec2: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAwsEc2InstanceRequest
      ) =>
        snapshotsApi.restoreEc2Instance(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      restoreImage: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreImageInput
      ) =>
        restoreImageApi.restoreImage(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    azureVm: {
      checkNameExists: (
        cloudAccountId?: string,
        resourceGroup?: string,
        vmName?: string
      ) =>
        httpClient.executeWithConfig(
          (
            ...args: Parameters<
              typeof checkAzureVMNameExistsApi.checkAzureVMNameExists
            >
          ) =>
            !cloudAccountId || !resourceGroup || !vmName
              ? Promise.resolve(undefined)
              : checkAzureVMNameExistsApi.checkAzureVMNameExists(...args),
          baseUrl,
          '/azure/vm-name-exists',
          {
            keepPreviousData: false,
          },
          currentProjectId,
          {
            cloudAccountId: cloudAccountId || '',
            resourceGroup: resourceGroup || '',
            vmName: vmName || '',
          }
        ),
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzureVmInstanceRequest
      ) =>
        snapshotsApi.restoreAzureVmInstance(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      restoreDisk: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzureDiskRequest
      ) =>
        snapshotsApi.restoreAzureDisk(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    azureMssql: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzureSqlDatabaseRequest
      ) =>
        snapshotsApi.restoreAzureSqlDatabase(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    eks: {
      restoreNamespace: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreEksNamespaceInput
      ) =>
        restoreEksNamespaceApi.restoreEksNamespace(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    gcpVm: {
      restoreDisk: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreGcpDiskRequest
      ) =>
        snapshotsApi.restoreGcpDisk(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      restoreVm: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreGcpVmInstanceRequest
      ) =>
        snapshotsApi.restoreGcpVmInstance(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      listMachineTypes: (accountId: string, zone: string) =>
        httpClient.execute(
          listGcpMachineTypesApi.listGcpMachineTypes.bind(
            listGcpMachineTypesApi
          ),
          baseUrl,
          '/listGcpMachineTypes',
          accountId,
          zone,
          currentProjectId
        ),
      listEncryptionKeys: (
        accountId: string | undefined,
        region: string | undefined
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listGcpEncryptionKeysApi.listGcpEncryptionKeys
            >
          ) =>
            !accountId || !region
              ? Promise.resolve(undefined)
              : listGcpEncryptionKeysApi.listGcpEncryptionKeys(...args),
          baseUrl,
          '/listGcpEncryptionKeys',
          accountId || '',
          region || '',
          currentProjectId
        ),
    },
    gcpCloudSql: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreGcpCloudSqlRequest
      ) =>
        snapshotsApi.restoreGcpCloudSql(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
  };
};
